import { TitleButtonDivider } from '@/common/buttons/TitleButtonDivider';
import { EntityPerspective } from '@/common/entityFramework/entityPerspective/EntityPerspective';
import { useFilterButtons } from '@/common/entityFramework/entityPerspective/hooks/useFilterButtons';
import { MassOverwriteButton } from '@/common/entityFramework/entityPerspective/overwrite/MassOverwriteButton';
import { RowIcon } from '@/common/entityFramework/entityPerspective/support/RowIcon';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { usePrincipal } from '@/common/principal/PrincipalContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback, useEffect, useRef, useState } from 'react';

export const AbvMcdLiabPerspective = ({ overUnderScenId }) => {
  const entityPerspectiveRef = useRef();
  const rsrc = useRsrc();
  const module = useModuleFromRoute();
  const [earnedPeriodIds, setEarnedPeriodIds] = useState(null);
  const [recentEarnedPeriods, setRecentEarnedPeriods] = useState(null);
  const { hasRole } = usePrincipal();

  const views = [
    {
      name: 'Payments',
      dims: ['OVER_UNDER_SCEN', 'EARNED_PERIOD', 'PROG', 'STATE', 'PROD'],
      kpis: [
        'PYMT_AMT',
        'PYMT_ADJ',
        'TOTAL_PYMT',
        'CALC_LIAB',
        'FORECASTED_LIAB_ADJ',
        'TOTAL_FORECASTED_LIAB',
        'LIAB_ADJ',
        'ACTIVITY',
        'PROJ_PYMT_AMT',
      ],
      disabledDims: ['OVER_UNDER_SCEN'],
      numFrozenDims: 1,
      dimToPeriodicities: {
        EARNED_PERIOD: ['YEAR', 'QTR'],
      },
    },
  ];

  useEffect(() => {
    rsrc
      .data(module, 'OVER_UNDER_SCEN_VAL_QTR_DATA_PLUGIN', { entity: 'MCD_LIAB', overUnderScenId }, 'MCD_LIAB')
      .then((res) => setEarnedPeriodIds(res));
    rsrc
      .data(
        module,
        'OVER_UNDER_SCEN_RECENT_VAL_QTR_DATA_PLUGIN',
        { entity: 'MCD_LIAB', overUnderScenId, n: 3 },
        'MCD_LIAB',
      )
      .then((res) => setRecentEarnedPeriods(res));
  }, [overUnderScenId]);

  const { filterButtonStages, filterButtons, onViewReset } = useFilterButtons([
    {
      key: 'approved',
      icon: fas.faCheckCircle,
      colors: [null, 'green', 'gray'],
      toolTips: ['Approved and not approved', 'Approved', 'Not approved'],
    },
  ]);

  const augReqParam = useCallback(
    (reqParam) => {
      // Discrepancy
      const approvedButtonState = filterButtonStages.approved;
      if (approvedButtonState > 0) {
        reqParam.expr = ExprUtils.and(
          reqParam.expr,
          approvedButtonState === 1 ? ExprUtils.whereParams('approved', 1) : ExprUtils.isNot('approved', 1),
        );
      }
    },
    [filterButtonStages],
  );

  if (earnedPeriodIds === null || recentEarnedPeriods === null) return null;

  const defaultDimFilters = { OVER_UNDER_SCEN: [overUnderScenId], EARNED_PERIOD: earnedPeriodIds };

  const augButtons = (buttons) => {
    buttons.push(
      <MassOverwriteButton
        toolTip={'Adjust Forecasted Liability'}
        kpiCodes={['FORECASTED_LIAB_ADJ']}
        entity={'MCD_LIAB'}
        role={'GTN_MCD_LIAB_ADJ_SAVE'}
        defaultDimValues={{ OVER_UNDER_SCEN: overUnderScenId }}
      />,
    );
    buttons.push(
      <MassOverwriteButton
        toolTip={'Liability Adjustment'}
        kpiCodes={['LIAB_ADJ']}
        entity={'MCD_LIAB'}
        role={'GTN_LIAB_ADJ_SAVE'}
        defaultDimValues={{ OVER_UNDER_SCEN: overUnderScenId }}
      />,
      ...filterButtons,
      <TitleButtonDivider key="filterButtonDivider" />,
    );
  };

  const rowIconsFn = (data) => {
    let rowIcons = [];

    // Discrepancy row icons
    if (data.approved === 1) {
      rowIcons.push(<RowIcon key="approved" icon={fas.faCheckCircle} color="green" toolTip="Approved" />);
    } else if (data.approved > 0) {
      rowIcons.push(
        <RowIcon key="containsApproval" icon={fas.faCheckCircle} color="#90ee90" toolTip="Contains an approval" />,
      );
    }

    return rowIcons;
  };

  // Add context menu entries for payment transaction details
  const getContextMenuItems = (params) => {
    let contextMenus = entityPerspectiveRef.current.getContextMenuItems(params);

    const pymtItems = [
      {
        name: 'Payment Amounts',
        action: () =>
          entityPerspectiveRef.current.navigateToViewer(
            params,
            'PYMT_AMT',
            'Payment Amounts',
            'MCD_LIAB_VIEWER_PLUGIN',
          ),
        icon: fas.faListUl,
      },
      {
        name: 'Payment Adjustments',
        action: () =>
          entityPerspectiveRef.current.navigateToViewer(
            params,
            'PYMT_ADJ',
            'Payment Adjustments',
            'MCD_LIAB_VIEWER_PLUGIN',
          ),
        icon: fas.faListUl,
      },
    ];

    const adjItems = [
      {
        name: 'Forecasted Liability Adjustment Details',
        action: () =>
          entityPerspectiveRef.current.navigateToPerspectiveAdj(
            params,
            'FORECASTED_LIAB_ADJ',
            'Forecasted Liability Adjustment Details',
          ),
        icon: fas.faListUl,
      },
      {
        name: 'Liability Adjustment Details',
        action: () =>
          entityPerspectiveRef.current.navigateToPerspectiveAdj(params, 'LIAB_ADJ', 'Liability Adjustment Details'),
        icon: fas.faListUl,
      },
    ];

    if (hasRole('GTN_MCD_LIAB_ADJ_SAVE')) {
      contextMenus.push({
        name: 'Approve',
        action: () =>
          entityPerspectiveRef.current.openActionDialog(params, 'Approve', 'MCD_LIAB_APPROVE_ACTION_PLUGIN'),
        icon: fas.faCheckCircle,
      });

      // Only show "Calculate Liability" at the OVER_UNDER_SCEN dimension to ensure that the user has all necessary filters applied before performing the calculation
      if (params.node.data.dim === 'OVER_UNDER_SCEN') {
        contextMenus.push({
          name: 'Calculate Liability',
          action: () =>
            entityPerspectiveRef.current.openActionDialog(params, 'Calculate Liability', 'MCD_LIAB_CALC_ACTION_PLUGIN'),
          icon: fas.faCalculator,
        });
      }

      contextMenus.push({
        name: 'Adjust Forecasted Liability',
        action: () => entityPerspectiveRef.current.overwrite(params, 'FORECASTED_LIAB_ADJ'),
        icon: fas.faPencil,
      });
    }

    if (hasRole('GTN_LIAB_ADJ_SAVE')) {
      contextMenus.push({
        name: 'Adjust Liability',
        action: () => entityPerspectiveRef.current.overwrite(params, 'LIAB_ADJ'),
        icon: fas.faPencil,
      });
    }

    contextMenus.push(
      {
        name: 'Payment Transaction Details',
        subMenu: pymtItems,
        icon: fas.faListUl,
      },
      {
        name: 'Adjustment Details',
        subMenu: adjItems,
        icon: fas.faListUl,
      },
      {
        name: 'Export',
        action: () => entityPerspectiveRef.current.openExportDialog(params),
        icon: fas.faArrowCircleDown,
      },
    );

    return contextMenus;
  };

  return (
    <EntityPerspective
      ref={entityPerspectiveRef}
      views={views}
      defaultDimFilters={defaultDimFilters}
      augButtons={augButtons}
      entity={'MCD_LIAB'}
      rowIconsFn={rowIconsFn}
      augReqParam={augReqParam}
      onViewReset={onViewReset}
      gridOptions={{
        getContextMenuItems,
      }}
    />
  );
};
